import React, { Fragment } from "react";
import PropTypes from "prop-types";
import JobDetails from "./FormSteps/JobDetails";
import FeesAndSkills from "./FormSteps/FeesAndSkills";
import Description from "./FormSteps/Description";
import BenefitsAndRequirements from "./FormSteps/BenefitsAndRequirements";
import Interview from "./FormSteps/Interview";
import MustHaves from "./FormSteps/MustHaves";
import Notifications from "./FormSteps/Notifications";
import Audience from "./FormSteps/Audience";
import AgencyBriefing from "./FormSteps/AgencyBriefing";
import NumberOfPositions from "./FormSteps/NumberOfPositions";
import notificationRecipientsType from "./types/notificationRecipientsType";
import audienceMembersType from "./types/audienceMembersType";
import feedbackTimescaleOptionsType from "./types/feedbackTimescaleOptionsType";

const CurrentComponent = ({
  step,
  jobProfile,
  inputOnChange,
  jobTypeOnChange,
  salaryTypeOnChange,
  editorOnChange,
  agencyBriefingOnChange,
  checkBoxOnChange,
  benefitsOnChange,
  feedbackTimescaleOnChange,
  notificationRecipientsOnChange,
  sectorOptions,
  vacancyReasonOptions,
  currencyOptions,
  mustHavesOnChange,
  errors,
  audienceMembersOnChange,
  burstOnChange,
  isPortalEmployer,
  isLiveOnTheMarketplace,
}) => {
  const {
    interviewComments,
    interviewStages,
    feedbackTimescaleId,
    feedbackTimescaleOptions,
    cvLimitPerRecruiter,
    notificationRecipients,
    notificationRecipientIds,
    keySkills,
    audienceMemberIds,
    audienceMembers,
    burst,
    numberOfPositions,
  } = jobProfile;

  const handleCurrentComponent = () => {
    let currentComponent = null;

    if (step === 1) {
      currentComponent = (
        <JobDetails
          jobProfile={jobProfile}
          inputOnChange={inputOnChange}
          sectorOptions={sectorOptions}
          vacancyReasonOptions={vacancyReasonOptions}
          errors={errors}
        />
      );
    }

    if (step === 2) {
      currentComponent = (
        <FeesAndSkills
          jobProfile={jobProfile}
          inputOnChange={inputOnChange}
          jobTypeOnChange={jobTypeOnChange}
          salaryTypeOnChange={salaryTypeOnChange}
          errors={errors}
          currencyOptions={currencyOptions}
        />
      );
    }

    if (step === 3) {
      currentComponent = (
        <Description
          jobProfile={jobProfile}
          inputOnChange={editorOnChange}
          errors={errors}
        />
      );
    }

    if (step === 4) {
      currentComponent = (
        <AgencyBriefing
          agencyBriefing={jobProfile.agencyBriefing}
          inputOnChange={agencyBriefingOnChange}
          errors={errors}
        />
      );
    }

    if (step === 5) {
      currentComponent = (
        <BenefitsAndRequirements
          jobProfile={jobProfile}
          inputOnChange={checkBoxOnChange}
          benefitsOnChange={benefitsOnChange}
        />
      );
    }

    if (step === 6) {
      currentComponent = (
        <Interview
          interviewComments={interviewComments}
          interviewStages={interviewStages}
          feedbackTimescaleId={feedbackTimescaleId}
          feedbackTimescaleOptions={feedbackTimescaleOptions}
          cvLimitPerRecruiter={cvLimitPerRecruiter}
          inputOnChange={inputOnChange}
          feedbackTimescaleOnChange={feedbackTimescaleOnChange}
          errors={errors}
        />
      );
    }

    if (step === 7) {
      currentComponent = (
        <MustHaves
          keySkills={keySkills}
          mustHavesOnChange={mustHavesOnChange}
          errors={errors}
        />
      );
    }

    if (step === 8) {
      currentComponent = (
        <Notifications
          notificationRecipients={notificationRecipients}
          notificationRecipientIds={notificationRecipientIds}
          notificationRecipientsOnChange={notificationRecipientsOnChange}
          errors={errors}
        />
      );
    }

    if (step === 9) {
      currentComponent = (
        <NumberOfPositions
          numberOfPositions={numberOfPositions}
          inputOnChange={inputOnChange}
          errors={errors}
        />
      );
    }

    if (step === 10 && isPortalEmployer) {
      currentComponent = (
        <Audience
          audienceMembers={audienceMembers}
          audienceMemberIds={audienceMemberIds}
          burst={burst}
          isLiveOnTheMarketplace={isLiveOnTheMarketplace}
          audienceMembersOnChange={audienceMembersOnChange}
          burstOnChange={burstOnChange}
          errors={errors}
        />
      );
    }

    return currentComponent;
  };

  return <Fragment>{handleCurrentComponent()}</Fragment>;
};

CurrentComponent.propTypes = {
  step: PropTypes.number.isRequired,
  jobProfile: PropTypes.shape({
    interviewComments: PropTypes.string,
    interviewStages: PropTypes.number,
    feedbackTimescaleId: PropTypes.number,
    agencyBriefing: PropTypes.string,
    feedbackTimescaleOptions: feedbackTimescaleOptionsType,
    cvLimitPerRecruiter: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    notificationRecipients: notificationRecipientsType,
    notificationRecipientIds: PropTypes.arrayOf(PropTypes.number),
    keySkills: PropTypes.string,
    audienceMemberIds: PropTypes.arrayOf(PropTypes.number),
    audienceMembers: audienceMembersType,
    burst: PropTypes.bool,
    numberOfPositions: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
  }).isRequired,
  inputOnChange: PropTypes.func.isRequired,
  jobTypeOnChange: PropTypes.func.isRequired,
  salaryTypeOnChange: PropTypes.func.isRequired,
  editorOnChange: PropTypes.func.isRequired,
  agencyBriefingOnChange: PropTypes.func.isRequired,
  checkBoxOnChange: PropTypes.func.isRequired,
  benefitsOnChange: PropTypes.func.isRequired,
  feedbackTimescaleOnChange: PropTypes.func.isRequired,
  notificationRecipientsOnChange: PropTypes.func.isRequired,
  sectorOptions: PropTypes.PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  vacancyReasonOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  mustHavesOnChange: PropTypes.func.isRequired,
  errors: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])
    .isRequired,
  audienceMembersOnChange: PropTypes.func.isRequired,
  burstOnChange: PropTypes.func.isRequired,
  isPortalEmployer: PropTypes.bool.isRequired,
  isLiveOnTheMarketplace: PropTypes.bool,
};

CurrentComponent.defaultProps = {
  isLiveOnTheMarketplace: null,
};

export default CurrentComponent;
